import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ActionButton,
  ButtonModel,
  DrawerContext,
  IconSolidError,
  ModalContext,
  Text,
  Toaster,
} from '@notino/react-styleguide';
import { LoyaltyStateEnum } from '@notino/shared/definitions/types';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useIsDesktop } from '@utils/helpers';

import { useLoyaltyStateMutation } from '../../../hooks/useLoyaltyStateMutation';
import { useUserNewsletterStatus } from '../../../hooks/useUserNewsletterStatus';
import { loyaltyMessages } from '../../../messages';
import { trackLoyaltyJoin } from '../../../tracking';

import {
  FooterWrapper,
  NotinoClub,
  PolicyText,
  ProgramAndPrivacyLink,
} from './styled';

export const Footer = () => {
  const { data: hasNewsletter, loading } = useUserNewsletterStatus();

  const { closeDrawer } = DrawerContext.useDrawerContext();
  const { toggleModal } = ModalContext.useModalContext();

  const {
    product: { masterProductCode },
  } = useProductDetailContext();

  const [registerCustomerForLoyaltyProgram] = useLoyaltyStateMutation({
    onError: () =>
      Toaster.error({
        icon: (
          <IconSolidError color="icon.inverse" width="1rem" height="1rem" />
        ),
        content: <FormattedMessage {...loyaltyMessages.errorMessage} />,
      }),
    onCompleted: (data) =>
      Toaster.info({
        icon: 'IconRegularCheckmark',
        button: (
          <NotinoClub
            href={
              data === LoyaltyStateEnum.Active ? '/mynotino/club' : '/mynotino'
            }
            target="_blank"
          >
            <FormattedMessage {...loyaltyMessages.notinoClub} />
          </NotinoClub>
        ),
        content: (
          <Text typography="labelRegular400" color="text.inverse">
            <FormattedMessage {...loyaltyMessages.successMessage} />
          </Text>
        ),
      }),
  });

  const { formatMessage } = useIntl();

  const isDesktop = useIsDesktop();

  const handleAction = () => {
    void registerCustomerForLoyaltyProgram();

    trackLoyaltyJoin(masterProductCode, hasNewsletter ? 'Subscribe' : 'Enter');

    if (isDesktop) {
      toggleModal();
    } else {
      closeDrawer();
    }
  };

  return (
    <FooterWrapper>
      <PolicyText typography="labelSmall400">
        <FormattedMessage
          {...loyaltyMessages.policy}
          values={{
            loyalProgramLink: (chunks) => (
              <ProgramAndPrivacyLink
                target="_blank"
                href={formatMessage(loyaltyMessages.loyalProgramLink)}
              >
                {chunks}
              </ProgramAndPrivacyLink>
            ),
            privacyPolicyLink: (chunks) => (
              <ProgramAndPrivacyLink
                target="_blank"
                href={formatMessage(loyaltyMessages.privacyPolicyLink)}
              >
                {chunks}
              </ProgramAndPrivacyLink>
            ),
          }}
        />
      </PolicyText>

      <ActionButton
        buttonStyle={ButtonModel.Styles.primary}
        onClick={handleAction}
        fullWidth={true}
        isLoading={loading}
      >
        <FormattedMessage
          {...loyaltyMessages[hasNewsletter ? 'join' : 'subscribeNewsletter']}
        />
      </ActionButton>
    </FooterWrapper>
  );
};
